import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Kunde, Grab, Vertrag, Tier, CreateVertragsartikelRequest } from '../types';
import kundeService from '../services/kundeService';
import grabService from '../services/grabService';
import vertragService from '../services/vertragService';
import tierService from '../services/tierService';
import { vertragDefaultsFuerTier, LAUFZEIT_STANDARD_JAHRE } from '../utils/vertragDefaults';
import { ankunft, markiereNeu } from '../utils/ankunft';
import {
  Button, Card, Badge, LoadingScreen, EmptyState, Icon, Field, Input, DateInput, Select, Textarea, FormActions,
} from '../components/ui';
import AnredeSelect from '../components/AnredeSelect';
import KommentarSektion from '../components/KommentarSektion';
import RechnungSektion from '../components/RechnungSektion';
import DokumentSektion from '../components/DokumentSektion';
import TierFormular from '../components/TierFormular';
import PositionenErfassung, { PositionEntwurf, positionenZuRequests } from '../components/PositionenErfassung';
import RueckwirkendDialog, { brauchtRueckwirkendFrage } from '../components/RueckwirkendDialog';
import VerlaengerungErfassung, { standardVerlaengerung, verlaengerungZuRequest } from '../components/VerlaengerungErfassung';
import { ZurueckLink } from '../components/ZurueckLink';

// Lokal gehalten (statt Import aus der Gräber-Seite), um Import-Zyklen zu vermeiden.
const grabStatusLabels: Record<string, string> = {
  FREI: 'Frei', BELEGT: 'Belegt', RESERVIERT: 'Reserviert', IN_NUTZUNG: 'In Nutzung',
};
const grabStatusColors: Record<string, string> = {
  FREI: 'bg-green-100 text-green-800', BELEGT: 'bg-gray-200 text-gray-800',
  RESERVIERT: 'bg-yellow-100 text-yellow-800', IN_NUTZUNG: 'bg-blue-100 text-blue-800',
};

// ISO-Datum (yyyy-mm-dd) -> dd.mm.yyyy, ohne Zeitzonen-Verschiebung.
const fmtDatum = (iso?: string): string => {
  if (!iso) return '–';
  const [y, m, d] = iso.split('T')[0].split('-');
  return (y && m && d) ? `${d}.${m}.${y}` : iso;
};

const emptyVertrag = {
  tierId: '', unterschriftsdatum: '', laufzeitJahre: String(LAUFZEIT_STANDARD_JAHRE), notizen: '',
  positionen: [] as PositionEntwurf[],
  verlaengerung: standardVerlaengerung,
};

const kundeToForm = (k: Kunde) => ({
  anrede: k.anrede || '', vorname: k.vorname, nachname: k.nachname,
  strasse: k.strasse || '', hausnummer: k.hausnummer || '', plz: k.plz || '', ort: k.ort || '',
  telefon: k.telefon || '', mobil: k.mobil || '', email: k.email || '',
  notizen: k.notizen || '', active: k.active,
});

const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-gray-100 py-2.5 last:border-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-right text-sm font-medium text-gray-900">{value ?? '–'}</span>
  </div>
);

const SectionHeader: React.FC<{ title: React.ReactNode; action?: React.ReactNode }> = ({ title, action }) => (
  <div className="mb-3 flex items-center justify-between gap-2">
    <h2 className="text-base font-semibold text-gray-900">{title}</h2>
    {action}
  </div>
);

/**
 * Kunden-Detailseite mit Inline-Bearbeitung: Stammdaten direkt editierbar, Gräber per „+“
 * zuordnen/entfernen, und je Grab ein Vertrag inline anlegen (Kunde + Grab vorbelegt).
 */
const KundeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const kundeId = id ? parseInt(id) : NaN;

  const [kunde, setKunde] = useState<Kunde | null>(null);
  const [allGraeber, setAllGraeber] = useState<Grab[]>([]);
  // Verträge je Grab-ID (aktive zuerst).
  const [vertraegeByGrab, setVertraegeByGrab] = useState<Record<number, Vertrag[]>>({});
  const [loading, setLoading] = useState(true);

  // Inline-Bearbeitung Stammdaten
  const [editingKunde, setEditingKunde] = useState(false);
  const [kForm, setKForm] = useState<any>({});
  const [savingKunde, setSavingKunde] = useState(false);

  // Grab hinzufügen (Inline-Panel)
  const [addingGrab, setAddingGrab] = useState(false);
  const [grabQuery, setGrabQuery] = useState('');
  const [linking, setLinking] = useState(false);

  // Vertrag inline je Grab
  const [vertragGrabId, setVertragGrabId] = useState<number | null>(null);
  const [vForm, setVForm] = useState<any>(emptyVertrag);
  const [savingVertrag, setSavingVertrag] = useState(false);
  // Tiere des Grabs, für das gerade ein Vertrag angelegt wird – erst beim Öffnen geladen.
  const [vTiere, setVTiere] = useState<Tier[]>([]);
  // Tier direkt aus dem Vertragsformular anlegen, ohne die Seite zu verlassen.
  const [neuesTier, setNeuesTier] = useState(false);
  // Vertrag weit in der Vergangenheit: wartet auf die Antwort „ab jetzt oder rückwirkend?“.
  const [rueckFrage, setRueckFrage] = useState<{ grab: Grab; positionen: CreateVertragsartikelRequest[] } | null>(null);

  // silent: im Hintergrund nachladen, ohne Ladebildschirm (offene Inline-Formulare bleiben stehen).
  const load = useCallback(async (silent = false) => {
    if (!kundeId || Number.isNaN(kundeId)) return;
    if (!silent) setLoading(true);
    try {
      const [k, graeber] = await Promise.all([
        kundeService.getById(kundeId),
        grabService.getAll(),
      ]);
      const linked = graeber.filter(g => g.kundeId === kundeId);
      const contractLists = await Promise.all(linked.map(g => vertragService.getByGrab(g.id)));
      const map: Record<number, Vertrag[]> = {};
      linked.forEach((g, i) => {
        map[g.id] = [...contractLists[i]].sort((a, b) => Number(b.active) - Number(a.active));
      });
      setKunde(k);
      setAllGraeber(graeber);
      setVertraegeByGrab(map);
    } catch { toast.error('Kunde konnte nicht geladen werden'); }
    finally { setLoading(false); }
  }, [kundeId]);

  useEffect(() => { load(); }, [load]);

  const graeber = useMemo(() => allGraeber.filter(g => g.kundeId === kundeId), [allGraeber, kundeId]);
  const linkedIds = useMemo(() => graeber.map(g => g.id), [graeber]);

  // Noch nicht zugeordnete Gräber für das Hinzufügen-Panel (nach Suche gefiltert).
  const addOptions = useMemo(() => {
    const q = grabQuery.trim().toLowerCase();
    return allGraeber
      .filter(g => g.kundeId !== kundeId)
      .filter(g => !q || `${g.friedhofName ?? ''} ${g.abteilung}/${g.grabnummer}`.toLowerCase().includes(q))
      .sort((a, b) =>
        (a.friedhofName ?? '').localeCompare(b.friedhofName ?? '')
        || a.abteilung.localeCompare(b.abteilung, undefined, { numeric: true })
        || a.grabnummer.localeCompare(b.grabnummer, undefined, { numeric: true }))
      .slice(0, 50);
  }, [allGraeber, kundeId, grabQuery]);

  // --- Stammdaten ---
  const startEditKunde = () => { if (kunde) { setKForm(kundeToForm(kunde)); setEditingKunde(true); } };
  const saveKunde = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingKunde(true);
    try {
      const data: any = { ...kForm };
      Object.keys(data).forEach(k => { if (data[k] === '') data[k] = undefined; });
      data.vorname = kForm.vorname; data.nachname = kForm.nachname;
      await kundeService.update(kundeId, { ...data, active: kForm.active });
      toast.success('Kunde gespeichert');
      markiereNeu(`kunde-${kundeId}`);
      setEditingKunde(false);
      await load();
    } catch (error: any) { toast.error(error?.response?.data?.error || 'Kunde konnte nicht gespeichert werden'); }
    finally { setSavingKunde(false); }
  };

  // --- Gräber zuordnen / entfernen ---
  const linkGrab = async (grab: Grab) => {
    setLinking(true);
    try {
      await kundeService.setGraeber(kundeId, [...linkedIds, grab.id]);
      toast.success(`Grab ${grab.abteilung}/${grab.grabnummer} zugeordnet`);
      markiereNeu(`grab-${grab.id}`);
      setGrabQuery('');
      setAddingGrab(false);
      await load();
    } catch (error: any) { toast.error(error?.response?.data?.error || 'Grab konnte nicht zugeordnet werden'); }
    finally { setLinking(false); }
  };

  const unlinkGrab = async (grab: Grab) => {
    if (!window.confirm(`Grab ${grab.abteilung}/${grab.grabnummer} von diesem Kunden entfernen?\nBestehende Verträge bleiben unverändert.`)) return;
    try {
      await kundeService.setGraeber(kundeId, linkedIds.filter(x => x !== grab.id));
      toast.success(`Grab ${grab.abteilung}/${grab.grabnummer} entfernt`);
      await load();
    } catch (error: any) { toast.error(error?.response?.data?.error || 'Grab konnte nicht entfernt werden'); }
  };

  // --- Vertrag anlegen ---
  /**
   * Tierauswahl setzt zugleich die Vorschlagswerte: Unterschrift = Beisetzung, Laufzeit nach
   * Gewicht (siehe utils/vertragDefaults). Beides bleibt im Formular frei änderbar.
   */
  const waehleTier = (tier?: Tier) => {
    const { unterschriftsdatum, laufzeitJahre } = vertragDefaultsFuerTier(tier);
    setVForm((f: any) => ({
      ...f,
      tierId: tier ? String(tier.id) : '',
      unterschriftsdatum,
      laufzeitJahre: String(laufzeitJahre),
    }));
  };

  const openVertrag = async (grab: Grab) => {
    setVForm(emptyVertrag);
    setVertragGrabId(grab.id);
    setVTiere([]);
    setNeuesTier(false);
    try {
      const tiere = await tierService.getAllByGrab(grab.id);
      setVTiere(tiere);
      // Ein Grab kann mehrere Tiere fassen; nur bei genau einem freien Tier lässt sich eindeutig
      // vorbelegen, sonst wählt der Mitarbeiter selbst.
      const frei = tiere.filter(t => t.vertragStatus !== 'AKTIV');
      if (frei.length === 1) waehleTier(frei[0]);
    } catch { toast.error('Tiere des Grabs konnten nicht geladen werden'); }
  };

  const tierAngelegt = async (tier: Tier) => {
    toast.success('Tier angelegt');
    markiereNeu(`tier-${tier.id}`);
    setVTiere(ts => [...ts, tier]);
    waehleTier(tier);
    setNeuesTier(false);
    // Grab-Karte aktualisieren: neues Tier in der Kopfzeile, Status jetzt „Belegt“.
    await load(true);
  };

  const submitVertrag = async (e: React.FormEvent, grab: Grab) => {
    e.preventDefault();
    const positionen = positionenZuRequests(vForm.positionen);
    if (typeof positionen === 'string') { toast.error(positionen); return; }
    const verlaengerung = verlaengerungZuRequest(vForm.verlaengerung);
    if (typeof verlaengerung === 'string') { toast.error(verlaengerung); return; }
    if (verlaengerung) positionen.push(verlaengerung);
    if (brauchtRueckwirkendFrage(vForm.unterschriftsdatum, positionen.length > 0)) {
      setRueckFrage({ grab, positionen });
      return;
    }
    await anlegeVertrag(grab, positionen);
  };

  const anlegeVertrag = async (grab: Grab, positionen: CreateVertragsartikelRequest[], abrechnungAb?: string) => {
    setSavingVertrag(true);
    try {
      const vertrag = await vertragService.create({
        kundeId,
        grabId: grab.id,
        // Ohne tierId taucht der Vertrag weder am Tier noch in der Grab-Historie auf.
        tierId: vForm.tierId ? parseInt(vForm.tierId) : undefined,
        unterschriftsdatum: vForm.unterschriftsdatum,
        laufzeitJahre: parseInt(vForm.laufzeitJahre) || LAUFZEIT_STANDARD_JAHRE,
        notizen: vForm.notizen || undefined,
        autoVerlaengerung: vForm.verlaengerung.aktiv,
        positionen,
        erstrechnung: positionen.length > 0,
        abrechnungAb,
      });
      // Auch vor dem Sprung zum Rechnungsentwurf: beim Zurückkommen leuchtet der Vertrag noch.
      markiereNeu(`vertrag-${vertrag.id}`);
      setVertragGrabId(null);
      setRueckFrage(null);
      // Nächster Schritt sind die Rechnungsentwürfe: prüfen und stellen.
      if (vertrag.erstrechnungId) {
        const anzahl = vertrag.erstrechnungIds?.length ?? 1;
        toast.success(anzahl > 1
          ? `Vertrag angelegt, dazu ${anzahl} Rechnungsentwürfe (je Vertragsjahr). Bitte prüfen und stellen.`
          : 'Vertrag angelegt. Bitte den Rechnungsentwurf prüfen und stellen.', { duration: 6000 });
        navigate(anzahl > 1 ? '/rechnungen' : `/rechnungen/${vertrag.erstrechnungId}`);
        return;
      }
      if (vertrag.erstrechnungHinweis) toast(vertrag.erstrechnungHinweis, { icon: 'ℹ️', duration: 8000 });
      else toast.success('Vertrag angelegt');
      await load();
    } catch (error: any) { toast.error(error?.response?.data?.error || 'Vertrag konnte nicht angelegt werden'); }
    finally { setSavingVertrag(false); }
  };

  if (loading) return <LoadingScreen />;
  if (!kunde) return <div className="p-6">Kunde nicht gefunden. <Link to="/kunden" className="text-brand-600">Zurück</Link></div>;

  const adresse = [
    [kunde.strasse, kunde.hausnummer].filter(Boolean).join(' '),
    [kunde.plz, kunde.ort].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');

  const handleKundeDelete = async () => {
    if (!window.confirm(`Kunde „${kunde.vorname} ${kunde.nachname}“ löschen?`)) return;
    try {
      await kundeService.delete(kundeId);
      toast.success('Kunde gelöscht');
      navigate('/kunden');
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Kunde konnte nicht gelöscht werden'); }
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <ZurueckLink fallback="/kunden" fallbackLabel="Zurück zu den Kunden" />

      <div className="mb-6 mt-3 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          {kunde.anrede ? `${kunde.anrede} ` : ''}{kunde.vorname} {kunde.nachname}
        </h1>
        <Badge className="bg-brand-100 text-brand-800">{kunde.kundennummer}</Badge>
        {!kunde.active && <Badge className="bg-gray-200 text-gray-700">Inaktiv</Badge>}
        <Button variant="secondary" size="sm" onClick={handleKundeDelete} className="ml-auto !text-red-600 hover:!bg-red-50" icon={<Icon.Trash className="h-4 w-4" />}>Löschen</Button>
      </div>

      {/* Stammdaten (inline editierbar) */}
      <Card ref={ankunft(`kunde-${kundeId}`).ref} className={`mb-6 p-6 ${ankunft(`kunde-${kundeId}`).className}`}>
        <SectionHeader
          title="Kundendaten"
          action={!editingKunde && (
            <button type="button" onClick={startEditKunde} className="rounded-md px-2 py-0.5 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-50">Bearbeiten</button>
          )}
        />
        {editingKunde ? (
          <form onSubmit={saveKunde} className="ui-aufklappen space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <Field label="Anrede"><AnredeSelect value={kForm.anrede} onChange={anrede => setKForm({ ...kForm, anrede })} /></Field>
              <Field label="Nachname" required><Input required value={kForm.nachname} onChange={e => setKForm({ ...kForm, nachname: e.target.value })} /></Field>
              <Field label="Vorname" required><Input required value={kForm.vorname} onChange={e => setKForm({ ...kForm, vorname: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <Field label="Straße" className="col-span-3"><Input value={kForm.strasse} onChange={e => setKForm({ ...kForm, strasse: e.target.value })} /></Field>
              <Field label="Nr."><Input value={kForm.hausnummer} onChange={e => setKForm({ ...kForm, hausnummer: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="PLZ"><Input value={kForm.plz} onChange={e => setKForm({ ...kForm, plz: e.target.value })} /></Field>
              <Field label="Ort" className="col-span-2"><Input value={kForm.ort} onChange={e => setKForm({ ...kForm, ort: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Telefon"><Input value={kForm.telefon} onChange={e => setKForm({ ...kForm, telefon: e.target.value })} /></Field>
              <Field label="Mobil"><Input value={kForm.mobil} onChange={e => setKForm({ ...kForm, mobil: e.target.value })} /></Field>
              <Field label="E-Mail"><Input type="email" value={kForm.email} onChange={e => setKForm({ ...kForm, email: e.target.value })} /></Field>
            </div>
            <Field label="Notizen"><Textarea rows={2} value={kForm.notizen} onChange={e => setKForm({ ...kForm, notizen: e.target.value })} /></Field>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={kForm.active} onChange={e => setKForm({ ...kForm, active: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
              Aktiv
            </label>
            <FormActions>
              <Button type="button" variant="secondary" onClick={() => setEditingKunde(false)}>Abbrechen</Button>
              <Button type="submit" loading={savingKunde}>Speichern</Button>
            </FormActions>
          </form>
        ) : (
          <>
            <Row label="Name" value={`${kunde.anrede ? kunde.anrede + ' ' : ''}${kunde.vorname} ${kunde.nachname}`} />
            <Row label="Kundennummer" value={kunde.kundennummer} />
            <Row label="Adresse" value={adresse || undefined} />
            <Row label="Telefon" value={kunde.telefon} />
            <Row label="Mobil" value={kunde.mobil} />
            <Row label="E-Mail" value={kunde.email} />
            <Row label="Notizen" value={kunde.notizen} />
          </>
        )}
      </Card>

      {/* Verknüpfte Gräber + Verträge */}
      <SectionHeader
        title={<>Verknüpfte Gräber{graeber.length > 0 ? ` (${graeber.length})` : ''}</>}
        action={
          <Button size="sm" variant="secondary" onClick={() => setAddingGrab(v => !v)} icon={<Icon.Plus className="h-4 w-4" />}>
            Grab zuordnen
          </Button>
        }
      />

      {/* Inline-Panel zum Hinzufügen eines Grabs */}
      {addingGrab && (
        <Card className="ui-aufklappen mb-4 p-0">
          <div className="relative border-b border-gray-200">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><Icon.Search className="h-4 w-4" /></span>
            <input
              type="text"
              autoFocus
              value={grabQuery}
              onChange={e => setGrabQuery(e.target.value)}
              placeholder="Grab suchen (Friedhof, Abteilung, Nummer)…"
              className="w-full rounded-t-xl border-0 py-2.5 pl-9 pr-3 text-sm focus:ring-0"
            />
          </div>
          <div className="max-h-60 overflow-y-auto p-1.5">
            {addOptions.length === 0 ? (
              <p className="px-2 py-3 text-sm text-gray-400">{grabQuery.trim() ? 'Keine Treffer für diese Suche.' : 'Keine weiteren Gräber vorhanden.'}</p>
            ) : addOptions.map(g => (
              <button
                key={g.id}
                type="button"
                disabled={linking}
                onClick={() => linkGrab(g)}
                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors hover:bg-brand-50 disabled:opacity-50"
              >
                <Icon.Plus className="h-4 w-4 text-brand-600" />
                <span className="text-gray-900">{g.friedhofName ? `${g.friedhofName} · ` : ''}{g.abteilung}/{g.grabnummer}</span>
                <Badge className={`ml-auto ${grabStatusColors[g.status] || ''}`}>{grabStatusLabels[g.status] || g.status}</Badge>
                {g.kundeId != null && <span className="text-xs text-amber-600" title="Aktuell einem anderen Kunden zugeordnet">→ {g.kundeName}</span>}
              </button>
            ))}
          </div>
        </Card>
      )}

      {graeber.length === 0 ? (
        <Card className="p-0">
          <EmptyState
            icon={<Icon.MapPin />}
            title="Noch keine Gräber zugeordnet"
            hint="Hier stehen die Gräber dieses Kunden mit ihren Verträgen."
            action={!addingGrab && (
              <Button onClick={() => setAddingGrab(true)} icon={<Icon.Plus className="h-4 w-4" />}>Grab zuordnen</Button>
            )}
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {graeber.map(grab => {
            const vertraege = vertraegeByGrab[grab.id] ?? [];
            const adding = vertragGrabId === grab.id;
            const a = ankunft(`grab-${grab.id}`);
            // Card reicht kein ref durch – daher der Wrapper fürs Ins-Bild-Scrollen.
            return (
              <div key={grab.id} ref={a.ref}>
              <Card className={`p-5 ${a.className}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <Icon.MapPin className="h-5 w-5 text-gray-400" />
                  <Link to={`/graeber/${grab.id}`} className="text-base font-semibold text-brand-700 hover:underline">{grab.abteilung}/{grab.grabnummer}</Link>
                  {grab.friedhofName && <span className="text-sm text-gray-500">{grab.friedhofName}</span>}
                  <Badge className={grabStatusColors[grab.status] || ''}>{grabStatusLabels[grab.status] || grab.status}</Badge>
                  {(grab.tiere && grab.tiere.length > 0) && (
                    <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                      <Icon.Paw className="h-4 w-4 text-gray-400" />
                      {grab.tiere.map((t, i) => (
                        <React.Fragment key={t.id}>
                          {i > 0 && ', '}
                          <span className={`rounded px-0.5 ${ankunft(`tier-${t.id}`).className}`}>{t.name || 'Tier'}{t.art ? ` (${t.art})` : ''}</span>
                        </React.Fragment>
                      ))}
                    </span>
                  )}
                  <div className="ml-auto flex items-center gap-1">
                    {!adding && (
                      <Button size="sm" variant="secondary" onClick={() => openVertrag(grab)} icon={<Icon.Plus className="h-4 w-4" />}>
                        Vertrag anlegen
                      </Button>
                    )}
                    <button
                      type="button"
                      onClick={() => unlinkGrab(grab)}
                      title="Grab von diesem Kunden entfernen"
                      aria-label="Grab entfernen"
                      className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                    >
                      <Icon.Trash className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Bestehende Verträge */}
                {vertraege.length > 0 && (
                  <div className="mt-4 space-y-2 border-t border-gray-100 pt-4">
                    {vertraege.map(v => { const va = ankunft(`vertrag-${v.id}`); return (
                      <Link
                        key={v.id}
                        ref={va.ref}
                        to={`/vertraege/${v.id}`}
                        state={{ from: 'kunde' }}
                        className={`${va.className} flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-gray-200 px-3 py-2 text-sm transition-colors hover:border-brand-300 hover:bg-brand-50/40`}
                      >
                        <span className="font-medium text-gray-900">{v.vertragsnummer}</span>
                        {v.status === 'BEENDET' && <Badge className="bg-gray-200 text-gray-700">Beendet</Badge>}
                        {!v.active && <Badge className="bg-gray-200 text-gray-700">Inaktiv</Badge>}
                        <span className="text-gray-500">Unterschrift: {fmtDatum(v.unterschriftsdatum)}</span>
                        <span className="text-gray-500">Laufzeit: {v.laufzeitJahre} J.</span>
                        {v.kundeId !== kundeId && (
                          <span className="text-amber-600" title="Vertrag läuft auf einen anderen Kunden">anderer Kunde: {v.kundeName}</span>
                        )}
                        <Icon.ChevronRight className="ml-auto h-4 w-4 text-gray-400" />
                      </Link>
                    ); })}
                  </div>
                )}

                {/* Inline-Formular: Vertrag zu diesem Grab anlegen */}
                {adding && neuesTier && (
                  <div className="ui-aufklappen mt-4 border-t border-gray-100 pt-4">
                    <p className="mb-3 text-sm font-medium text-gray-700">Neues Tier für {grab.abteilung}/{grab.grabnummer}</p>
                    <TierFormular grabId={grab.id} onSaved={tierAngelegt} onCancel={() => setNeuesTier(false)} />
                  </div>
                )}

                {adding && !neuesTier && (
                  <form onSubmit={e => submitVertrag(e, grab)} className="ui-aufklappen mt-4 space-y-4 border-t border-gray-100 pt-4">
                    <h3 className="text-base font-semibold text-gray-900">Neuer Vertrag für {grab.abteilung}/{grab.grabnummer}</h3>
                    <Field label="Tier">
                      <Select
                        value={vForm.tierId}
                        onChange={e => waehleTier(vTiere.find(t => String(t.id) === e.target.value))}
                      >
                        <option value="">Kein Tier</option>
                        {vTiere.map(t => {
                          const unterVertrag = t.vertragStatus === 'AKTIV';
                          const beschreibung = [t.art, t.gewicht != null ? `${t.gewicht} kg` : null]
                            .filter(Boolean).join(', ');
                          return (
                            <option key={t.id} value={t.id} disabled={unterVertrag}>
                              {t.name || 'Tier'}
                              {beschreibung ? ` (${beschreibung})` : ''}
                              {unterVertrag ? ' – bereits unter Vertrag' : ''}
                            </option>
                          );
                        })}
                      </Select>
                      <button
                        type="button"
                        onClick={() => setNeuesTier(true)}
                        className="-ml-1 mt-0.5 inline-flex min-h-[44px] items-center gap-1 px-1 text-sm font-medium text-brand-700 hover:underline"
                      >
                        <Icon.Plus className="h-4 w-4" />
                        {vTiere.length === 0 ? 'Noch kein Tier an diesem Grab – jetzt anlegen' : 'Neues Tier anlegen'}
                      </button>
                    </Field>
                    <div>
                      <div className="grid grid-cols-2 gap-4">
                        <Field label="Unterschriftsdatum" required>
                          <DateInput required value={vForm.unterschriftsdatum} onChange={v => setVForm({ ...vForm, unterschriftsdatum: v })} />
                        </Field>
                        <Field label="Laufzeit (Jahre)" required>
                          <Input type="number" min={1} required value={vForm.laufzeitJahre} onChange={e => setVForm({ ...vForm, laufzeitJahre: e.target.value })} />
                        </Field>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">Vorschlag aus dem Tier: Unterschrift = Beisetzung, Laufzeit 5 Jahre ab 25 kg, sonst 3 – frei änderbar.</p>
                    </div>
                    <PositionenErfassung
                      positionen={vForm.positionen}
                      onChange={positionen => setVForm((f: any) => ({ ...f, positionen }))}
                      unterschriftsdatum={vForm.unterschriftsdatum || undefined}
                    >
                      <VerlaengerungErfassung
                        wert={vForm.verlaengerung}
                        onChange={verlaengerung => setVForm((f: any) => ({ ...f, verlaengerung }))}
                        unterschriftsdatum={vForm.unterschriftsdatum || undefined}
                        laufzeitJahre={parseInt(vForm.laufzeitJahre) || undefined}
                      />
                    </PositionenErfassung>
                    <Field label="Notizen"><Textarea rows={2} value={vForm.notizen} onChange={e => setVForm({ ...vForm, notizen: e.target.value })} /></Field>
                    <p className="text-xs text-gray-500">
                      Beim Anlegen entsteht der Entwurf der ersten Rechnung – Sie prüfen und stellen ihn danach. Ein freies Grab wird auf „In Nutzung“ gesetzt.
                    </p>
                    <FormActions>
                      <Button type="button" variant="secondary" onClick={() => setVertragGrabId(null)}>Abbrechen</Button>
                      <Button type="submit" loading={savingVertrag}>Vertrag anlegen</Button>
                    </FormActions>
                  </form>
                )}
              </Card>
              </div>
            );
          })}
        </div>
      )}

      {rueckFrage && (
        <RueckwirkendDialog
          unterschriftsdatum={vForm.unterschriftsdatum}
          saving={savingVertrag}
          onWahl={ab => anlegeVertrag(rueckFrage.grab, rueckFrage.positionen, ab)}
          onAbbrechen={() => setRueckFrage(null)}
        />
      )}

      {/* Rechnungen des Kunden */}
      <div className="mt-6">
        <RechnungSektion kundeId={kunde.id} />
      </div>

      {/* Dokumente */}
      <div className="mt-6">
        <DokumentSektion entityType="KUNDE" entityId={kunde.id} />
      </div>

      {/* Kommentare */}
      <div className="mt-6">
        <KommentarSektion entityType="KUNDE" entityId={kunde.id} />
      </div>
    </div>
  );
};

export default KundeDetail;
